/**
 * TemporarySchedules — one-off per-worker schedule changes.
 *
 * Migrated to live API in Wave 3.2. Reads /v1/attendance/schedules/temporary
 * and POST/PATCH/DELETE the same surface.
 *
 * Status for a temp Schedule row:
 *   - effective_to < today   → Expired
 *   - effective_from <= today <= effective_to → Active
 *   - effective_from > today → Scheduled (upcoming)
 *
 * Unlike DepartmentSchedules / WorkerSchedules, the backend exposes
 * an actual PATCH for temp rows, so editing reuses the row rather than
 * creating a new one.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, IconButton, InputLabel,
  MenuItem, Paper, Select, Snackbar, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow, TextField,
  Tooltip, Typography,
} from '@mui/material';
import {
  Add as AddIcon, CloseOutlined, DeleteOutlined, EditOutlined,
  RefreshOutlined,
} from '@mui/icons-material';
import { ApiError, attendanceApi, organizationApi } from '../../../api';
import type {
  Schedule, TempScheduleCreate, TempScheduleUpdate, Timetable,
} from '../../../api/attendance';
import type { WorkerResponse } from '../../../api/organization';
import { ConfirmDialog } from '../../../components/primitives';
import { useTranslation } from 'react-i18next';

import DateField from '../../../components/inputs/DateField';
import { translateApiError } from '../../../i18n/translateApiError';
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return `${first}${last}`.toUpperCase() || '•';
}

function fullName(e: WorkerResponse): string {
  return `${e.first_name} ${e.last_name}`.trim();
}

type ScheduleStatus = 'scheduled' | 'active' | 'expired';

function statusOf(s: Schedule): ScheduleStatus {
  const today = new Date().toISOString().slice(0, 10);
  const from = (s.effective_from as string | undefined) ?? '';
  const to = (s.effective_to as string | null | undefined) ?? from;
  if (!from) return 'scheduled';
  if (to && to < today) return 'expired';
  if (from > today) return 'scheduled';
  return 'active';
}

function statusColor(s: ScheduleStatus): 'success' | 'info' | 'default' {
  if (s === 'active') return 'success';
  if (s === 'scheduled') return 'info';
  return 'default';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const TemporarySchedules: React.FC = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<Schedule[]>([]);
  const [workers, setWorkers] = useState<WorkerResponse[]>([]);
  const [timetables, setTimetables] = useState<Timetable[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Schedule | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<{
    worker_id: string;
    timetable_id: string;
    effective_from: string;
    effective_to: string;
    reason: string;
  }>({
    worker_id: '',
    timetable_id: '',
    effective_from: new Date().toISOString().slice(0, 10),
    effective_to: new Date().toISOString().slice(0, 10),
    reason: '',
  });
  const [pendingDelete, setPendingDelete] = useState<Schedule | null>(null);

  // ---------- data ----------
  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [temps, workers, tts] = await Promise.all([
        attendanceApi.schedulesApi.listTemporarySchedules(),
        organizationApi.workersApi.list('active'),
        attendanceApi.timetablesApi.list(),
      ]);
      setRows(temps);
      setWorkers(workers);
      setTimetables(tts);
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('attendance:tempSchedules.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const workerById = useMemo(
    () => new Map(workers.map((e) => [e.id, e])),
    [workers],
  );
  const timetableById = useMemo(
    () => new Map(timetables.map((tt) => [tt.id, tt])),
    [timetables],
  );

  const enriched = useMemo(() => rows.map((r) => {
    const worker = r.worker_id ? workerById.get(r.worker_id) : undefined;
    const timetable = r.timetable_id ? timetableById.get(r.timetable_id) : undefined;
    return {
      schedule: r,
      workerName: worker ? fullName(worker) : `Worker ${r.worker_id ?? ''}`,
      timetableName: timetable ? timetable.name : '—',
      status: statusOf(r),
    };
  }), [rows, workerById, timetableById]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return enriched;
    return enriched.filter(
      (r) =>
        r.workerName.toLowerCase().includes(q) ||
        (r.schedule.reason ?? '').toLowerCase().includes(q),
    );
  }, [enriched, filter]);

  const paginated = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage],
  );

  // ---------- mutations ----------
  const handleOpenAdd = () => {
    setEditing(null);
    setForm({
      worker_id: '',
      timetable_id: '',
      effective_from: new Date().toISOString().slice(0, 10),
      effective_to: new Date().toISOString().slice(0, 10),
      reason: '',
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (s: Schedule) => {
    setEditing(s);
    setForm({
      worker_id: s.worker_id ?? '',
      timetable_id: s.timetable_id ?? '',
      effective_from: (s.effective_from as string | undefined) ?? '',
      effective_to:
        (s.effective_to as string | null | undefined) ??
        (s.effective_from as string | undefined) ??
        '',
      reason: s.reason ?? '',
    });
    setDialogOpen(true);
  };

  const canSave =
    !!form.worker_id && !!form.timetable_id &&
    !!form.effective_from && !!form.effective_to &&
    form.effective_to >= form.effective_from;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      if (editing) {
        const patch: TempScheduleUpdate = {
          timetable_id: form.timetable_id,
          effective_from: form.effective_from,
          effective_to: form.effective_to,
          reason: form.reason || null,
        };
        await attendanceApi.schedulesApi.updateTemporarySchedule(editing.id, patch);
        setToast(t('attendance:tempSchedules.toastUpdated'));
      } else {
        const create: TempScheduleCreate = {
          worker_id: form.worker_id,
          timetable_id: form.timetable_id,
          effective_from: form.effective_from,
          effective_to: form.effective_to,
          reason: form.reason || null,
        };
        await attendanceApi.schedulesApi.createTemporarySchedule(create);
        setToast(t('attendance:tempSchedules.toastCreated'));
      }
      setDialogOpen(false);
      await fetchAll();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await attendanceApi.schedulesApi.deleteTemporarySchedule(pendingDelete.id);
      setToast(t('attendance:tempSchedules.toastRemoved'));
      await fetchAll();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('attendance:tempSchedules.removeFailed'));
      throw e;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      {/* Header */}
      <Box sx={{ width: '100%', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 1.5, mb: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem', color: 'text.primary' }}>{t('attendance:tempSchedules.title')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('attendance:tempSchedules.subtitle')}</Typography>
          {/* How this layer interacts with the others.
              Same shape the Rules and Geofences pages already use — a second line of
              small text under the subtitle, not a diagram or a dialog. Added 2026-08-10
              on owner review: those two modules explain their override order and the
              three schedule pages explained nothing, while the schedule chain is the one
              with the surprises. Each sentence answers a specific way an admin gets it
              wrong, and no page repeats the whole chain (a temporary schedule is always a WORKING day — it cannot express time off, which is the thing admins try first). */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            {t('attendance:tempSchedules.precedence')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Tooltip title={t('common:actions.refresh')}>
            <IconButton size="small" onClick={fetchAll} disabled={loading}
              sx={{ border: '1px solid rgba(0,0,0,0.2)', borderRadius: 1, bgcolor: '#ffffff', '&:hover': { bgcolor: '#f5f5f5' } }}>
              <RefreshOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} size="small" onClick={handleOpenAdd}
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}>
            {t('attendance:tempSchedules.addButton')}
          </Button>
        </Stack>
      </Box>

      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <TextField size="small" variant="outlined" label={t('attendance:tempSchedules.filterLabel', { defaultValue: 'Filter (Worker, Reason)' })} value={filter}
          onChange={(e) => { setFilter(e.target.value); setPage(0); }}
          sx={{ width: 400, maxWidth: '100%', mr: 2 }} />
        <TablePagination component="div" count={filtered.length} page={page}
          onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[5, 10, 25]} />
      </Box>

      {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} sx={{ width: '100%', borderRadius: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead sx={{ bgcolor: 'grey.100', '& .MuiTableCell-root': { fontWeight: 600 } }}>
            <TableRow>
              <TableCell>{t("attendance:tempSchedules.colWorker")}</TableCell>
              <TableCell>{t('attendance:tempSchedules.colEffectiveRange', { defaultValue: 'Effective Range' })}</TableCell>
              <TableCell>{t('attendance:tempSchedules.colTimetable', { defaultValue: 'Temporary Timetable' })}</TableCell>
              <TableCell>{t("attendance:tempSchedules.colReason")}</TableCell>
              <TableCell align="center">{t('attendance:timeEntries.colStatus')}</TableCell>
              <TableCell align="center">{t("attendance:tempSchedules.colActions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : paginated.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                  <Typography variant="body2">{t('attendance:tempSchedules.noSchedules')}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginated.map((ts) => (
                <TableRow key={ts.schedule.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ width: 28, height: 28, fontSize: '0.7rem', bgcolor: 'primary.main' }}>
                        {initials(ts.workerName)}
                      </Avatar>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        {ts.workerName}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {(ts.schedule.effective_from as string | undefined) ?? '—'}
                      {ts.schedule.effective_to && ` → ${ts.schedule.effective_to as string}`}
                    </Typography>
                  </TableCell>
                  <TableCell>{ts.timetableName}</TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">{ts.schedule.reason ?? '—'}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={t(`common:status.${ts.status}`)} size="small" color={statusColor(ts.status)} variant="outlined"
                      sx={{ borderRadius: 1, height: 20, fontSize: '0.7rem' }} />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={t('common:actions.edit')}>
                      <span>
                        <IconButton size="small" disabled={ts.status === 'expired'}
                          onClick={() => handleOpenEdit(ts.schedule)}>
                          <EditOutlined fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t('attendance:tempSchedules.removeTooltip')}>
                      <IconButton size="small" onClick={() => setPendingDelete(ts.schedule)}>
                        <DeleteOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add / edit dialog */}
      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ m: 0, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem', color: 'text.primary' }}>
            {editing ? t('attendance:tempSchedules.editTitle') : t('attendance:tempSchedules.addTitle')}
          </Typography>
          <IconButton size="small" onClick={() => !saving && setDialogOpen(false)}>
            <CloseOutlined fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small" disabled={!!editing || workers.length === 0}>
              <InputLabel>{t("attendance:tempSchedules.workerLabel")}</InputLabel>
              <Select value={form.worker_id} label={t("attendance:tempSchedules.workerLabel")}
                onChange={(e) => setForm((f) => ({ ...f, worker_id: e.target.value }))}>
                {workers.map((worker) => (
                  <MenuItem key={worker.id} value={worker.id}>{fullName(worker)} ({worker.worker_code})</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small" disabled={timetables.length === 0}>
              <InputLabel>{t('attendance:tempSchedules.colTimetable')}</InputLabel>
              <Select value={form.timetable_id} label={t('attendance:tempSchedules.tempTimetableLabel')}
                onChange={(e) => setForm((f) => ({ ...f, timetable_id: e.target.value }))}>
                {timetables.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
              </Select>
            </FormControl>

            <Stack direction="row" spacing={1.5}>
              <DateField fullWidth size="small" label={t('attendance:tempSchedules.fromLabel')}
                value={form.effective_from}
                onChange={(e) => setForm((f) => ({ ...f, effective_from: e.target.value }))} />
              <DateField fullWidth size="small" label={t('attendance:tempSchedules.toLabel')}
                value={form.effective_to}
                onChange={(e) => setForm((f) => ({ ...f, effective_to: e.target.value }))} />
            </Stack>

            <TextField fullWidth size="small" label={t("attendance:tempSchedules.reasonLabel")} multiline rows={2}
              value={form.reason}
              onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} />

            {form.effective_from && form.effective_to && form.effective_to < form.effective_from && (
              <Alert severity="warning" variant="outlined" sx={{ fontSize: '0.75rem' }}>
                End date must be on or after the start date.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button size="small" onClick={() => setDialogOpen(false)} disabled={saving}
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}>{t("common:actions.cancel")}</Button>
          <Button variant="contained" size="small" onClick={handleSave} disabled={!canSave || saving}
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}>
            {saving ? <CircularProgress size={18} /> : t('common:actions.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!pendingDelete}
        tone="danger"
        title={t('attendance:tempSchedules.deleteTitle')}
        description={t('attendance:tempSchedules.deleteDescription')}
        confirmLabel={t('common:actions.delete')}
        confirmLoadingLabel={t('common:status.deleting')}
        onConfirm={handleConfirmDelete}
        onClose={() => setPendingDelete(null)}
      />

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="info" onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
};

export default TemporarySchedules;
